import { useState, useEffect } from "react";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import PasswordChecklist from "react-password-checklist";
import { ReactComponent as CheckImg } from "assets/images/check.svg";
import { ReactComponent as Xmark } from "assets/images/xmark.svg";
import { Input } from "components/ui/input/Input";
import * as Constant from "utilities/Constant";
import "../password_checker.scss";

export const passwordIcon = (isPasswordVisible: boolean) => (isPasswordVisible ? solid("eye") : solid("eye-slash"));

export const passwordType = (isPasswordVisible: boolean) => (isPasswordVisible ? "text" : "password");

// Allowed characters as per PingOne
const ALLOWED_SPECIAL_CHARS = `~!@#$%^&*()-_=+[]{}|;:,.<>/?`;
const ESCAPED_SPECIAL_CHARS = ALLOWED_SPECIAL_CHARS.replace(/[-[\]{}()*+?.,^$|#\s]/g, String.raw`\$&`);
const SPECIAL_CHARS_REGEX = new RegExp(`[${ESCAPED_SPECIAL_CHARS}]`);

interface PasswordCheckerProps {
  checkErrors?: boolean;
  onChange?: (...event: any[]) => void;
  createLabel?: string;
  setHasPasswordError?: (hasError: boolean) => void;
  setBtnVisible?: (btnIsValid: boolean) => void;
  createPasswordPlaceholderName?: string;
}

const PasswordCheckerVersion2 = ({
  checkErrors = true,
  onChange,
  createLabel,
  setHasPasswordError,
  setBtnVisible: setBtnIsValid,
  createPasswordPlaceholderName = "Password",
}: PasswordCheckerProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [passwordCheckerError, setPasswordCheckerError] = useState("");
  const [checkPasswordError, setCheckPasswordError] = useState({
    password: false,
  });
  const [password, setPassword] = useState<string>("");
  const [isValid, setIsValid] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [uniqueSpecialCharsValid, setUniqueSpecialCharsValid] = useState(false);

  const overallValid = isValid && uniqueSpecialCharsValid;

  useEffect(() => {
    if (checkErrors) {
      if (setHasPasswordError) {
        setHasPasswordError(passwordCheckerError !== "" || !uniqueSpecialCharsValid);
      }
      if (setBtnIsValid) {
        setBtnIsValid(!overallValid);
      }
    }
  }, [checkErrors, passwordCheckerError, setHasPasswordError, setBtnIsValid, overallValid, uniqueSpecialCharsValid]);

  const passwordChecklistStyle = overallValid ? " password-checkerv2--validFalse" : " password-checkerv2--validTrue";

  return (
    <div className="password-input-checker">
      <Input
        name="password_input"
        type={passwordType(isPasswordVisible)}
        placeholder={createPasswordPlaceholderName}
        icon={passwordIcon(isPasswordVisible)}
        label={createLabel}
        containerClassName
        onIconClick={() => setIsPasswordVisible(!isPasswordVisible)}
        onChange={(e: React.FormEvent<HTMLInputElement>) => {
          const value = e.currentTarget.value;
          setPassword(value);
          const uniqueCount = new Set(value.split("")).size;
          setUniqueSpecialCharsValid(uniqueCount >= 5);
          if (onChange) {
            onChange(e);
          }
        }}
        onBlur={() => {
          setCheckPasswordError((props) => ({
            ...props,
            password: true,
          }));
        }}
        isIconPointer
        inputBorderHighlightForError={checkPasswordError.password && !overallValid}
        onFocus={() => setIsFocus(true)}
      />

      {isFocus && (
        <div className={"input__wrapper no-margin-top password-checkerv2" + passwordChecklistStyle}>
          Password must include:
          <PasswordChecklist
            rules={["minLength", "capital", "lowercase", "number", "specialChar"]}
            minLength={Constant.PWD_MIN_LENGTH}
            value={password}
            specialCharsRegex={SPECIAL_CHARS_REGEX}
            messages={{
              minLength: Constant.NEW_PWD_CHECK_MIN_LENGTH,
              capital: `${Constant.NEW_PWD_CHECK_UPPER_CASE} (A - Z)`,
              lowercase: `${Constant.NEW_PWD_CHECK_LOWER_CASE} (a - z)`,
              number: `${Constant.NEW_PWD_CHECK_NUMBER} (0 - 9)`,
              specialChar: `Special character (${ALLOWED_SPECIAL_CHARS})`,
            }}
            onChange={(valid: boolean) => {
              setIsValid(valid);
              setPasswordCheckerError(valid ? "" : Constant.INVALID_PASSWORD_ERROR);
            }}
            iconComponents={{
              ValidIcon: <CheckImg className="validCheck margin-small" />,
              InvalidIcon: <Xmark className="invalidMark margin-small" />,
            }}
          />
          <ul className="custom-rule">
            <li
              className={uniqueSpecialCharsValid ? "valid" : "invalid"}
            >
              {uniqueSpecialCharsValid ? (
                <CheckImg className="validCheck margin-small" />
              ) : (
                <Xmark className="invalidMark margin-small" />
              )}
              <span>{Constant.NEW_PWD_CHECK_UNIQUE_SPECIAL_CHARS}</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordCheckerVersion2;
